import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { StarRatingDisplay } from './StarRatingDisplay'
import type { TestimonialDocument } from '@/types'

interface TestimonialCardProps {
  testimonial: TestimonialDocument & { id: string }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function TestimonialCard({ testimonial }: TestimonialCardProps) {
  const {
    content,
    clientName,
    clientTitle,
    serviceType,
    photoUrl,
    rating,
  } = testimonial

  return (
    <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-8 text-card-foreground">
      {/* Decorative open quote */}
      <span className="font-serif text-6xl leading-none text-brand-orange select-none" aria-hidden>
        &ldquo;
      </span>

      {/* Content */}
      <blockquote className="-mt-4 flex-1 text-lg leading-relaxed text-balance line-clamp-8">
        {content}
      </blockquote>

      {/* Rating */}
      <StarRatingDisplay rating={rating} size="md" />

      {/* Attribution */}
      <div className="flex items-center gap-3">
        <Avatar size="lg">
          {photoUrl ? (
            <AvatarImage src={photoUrl} alt={clientName} />
          ) : null}
          <AvatarFallback>{getInitials(clientName)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-semibold leading-tight">{clientName}</p>
          {clientTitle && (
            <p className="text-sm opacity-70">{clientTitle}</p>
          )}
          {serviceType && (
            <Badge variant="secondary" className="mt-1 text-xs">
              {serviceType}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}
